import React, { useEffect, useRef } from "react";
import {
  Folder,
  Download,
  Trash2,
  MoreVertical,
  Settings,
  FolderTree,
  Upload,
  RefreshCw,
} from "lucide-react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import changeStatusIcon from "../../../assets/icons/changeStatusIcon.png";
import { ITEM_TYPE } from "../constants/repoConstants";
import { canModifyRepoItem } from "../utils/canModifyRepoItem";
import { formatBytes } from "../utils/formatBytes";
import { getItemTypeDisplay } from "../utils/getItemTypeDisplay";

export const DraggableItem = ({
  item,
  viewMode,
  onNavigate,
  onToggleSelect,
  onAction,
  isSelected,
  getFileIcon,
  onMove,
  role,
  repoView,
  onApprovalStatusChange,
  dragSelectionRef,
  selectedDragItems,
  updatingItems = new Set(),
}) => {
  const clickTimerRef = useRef(null);
  const isBlockedDocumentSelection =
    role !== "admin" &&
    item?.type === "file" &&
    ["duplicate", "pending", "rejected"].includes(
      String(item?.approvalStatus || "").toLowerCase(),
    );

  // Check if user can move/modify this item
  // In the refined system, admins can modify everything. Regular users can modify personal items only.
  const canModify = canModifyRepoItem(item, role);
  const dragItems =
    isSelected && selectedDragItems.length > 0 ? selectedDragItems : [item];

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  const showChangeStatus =
    item.type === "file" &&
    !item.isPersonalSpace &&
    ((repoView === "global" && role === "admin") ||
      (repoView === "personal" && role !== "admin"));

  const isPersonalDeletableItem =
    item.isPersonalSpace === true ||
    (!item.isPersonalSpace &&
      item.ownerType !== "admin" &&
      item.mode !== "global" &&
      item.mode !== "group");

  const shouldHideDownload =
    item.type === "file" &&
    (item.fileType === "webpage" ||
      item.fileType === "youtube_audio" ||
      item.fileType === "text" ||
      item.fileType === "qa");

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ITEM_TYPE,
      item: () => ({
        id: item.id,
        type: item.type,
        name: item.name,
        items: dragItems,
        count: dragItems.length,
      }),
      canDrag: canModify,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [item, dragItems, canModify],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const getDraggedItems = (draggedItem) => draggedItem.items || [draggedItem];

  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    canDrop: (draggedItem) => {
      const draggedItems = getDraggedItems(draggedItem);
      const draggedIds = new Set(draggedItems.map((dragItem) => dragItem.id));
      return item.type === "folder" && !draggedIds.has(item.id) && canModify;
    },
    drop: (draggedItem) => onMove(getDraggedItems(draggedItem), item.id),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const combinedRef = (node) => {
    drag(node);
    if (item.type === "folder") drop(node);
  };

  if (viewMode === "grid") {
    return (
      <div
        ref={combinedRef}
        data-item-id={String(item.id)}
        className={`repo-card ${isSelected ? "selected" : ""} ${isOver ? "drop-target" : ""} ${!canModify ? "read-only" : ""}`}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        onClick={(e) => {
          if (isBlockedDocumentSelection) {
            return;
          }
          if (dragSelectionRef.current) {
            return;
          }
          // For folders, debounce to wait for potential double-click
          if (item.type === "folder") {
            if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
            clickTimerRef.current = setTimeout(() => {
              onToggleSelect(item.id);
              clickTimerRef.current = null;
            }, 300);
          } else {
            onToggleSelect(item.id);
          }
        }}
        onDoubleClick={(e) => {
          if (item.type === "folder") {
            // Clear pending single-click selection
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = null;
            }
            onNavigate(item.id, item.name);
          }
        }}
      >
        <div
          className="repo-card-checkbox"
          onClick={(e) => {
            if (isBlockedDocumentSelection) {
              return;
            }
            e.stopPropagation();
            onToggleSelect(item.id);
          }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            disabled={isBlockedDocumentSelection}
            aria-disabled={isBlockedDocumentSelection}
            style={{
              cursor: isBlockedDocumentSelection ? "not-allowed" : "pointer",
              opacity: isBlockedDocumentSelection ? 0.45 : 1,
            }}
          />
        </div>

        <div className="repo-card-icon mb-1">
          {item.type === "folder" ? (
            <Folder
              className="repo-folder-icon"
              size={48}
              fill="currentColor"
              fillOpacity={0.2}
            />
          ) : (
            getFileIcon(item, 48)
          )}
        </div>

        <div className="repo-card-content d-flex flex-column gap-1">
          <div className="repo-card-name" title={item.name}>
            {item.name?.charAt(0).toUpperCase() + item.name?.slice(1)}
          </div>
          <div className="repo-card-details">
            <span className="repo-card-type">{getItemTypeDisplay(item)}</span>
            {item.type === "file" && item.size && (
              <span className="repo-card-size">• {formatBytes(item.size)}</span>
            )}
          </div>
          <div className="repo-card-date">
            {new Date(item.updatedAt || item.createdAt).toLocaleDateString()}
          </div>
        </div>

        <div className="repo-card-footer">
          {item.type === "file" && (
            <span
              className={`badge  ${item.fileStatus === "1" ? "bg-success" : ""}`}
              
            >
              {item.fileStatus === "1" ? "Active" : "Inactive"}
            </span>
          )}
          {item.mode === "global" && (
            <span className="badge" >
              Global
            </span>
          )}
        </div>

        <div className="repo-card-actions">
          <div className="dropdown border-0">
            <button
              className="repo-action-dot bg-transparent border-0"
              data-bs-toggle="dropdown"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={16} />
            </button>
            <ul className="dropdown-menu">
              {item.type === "file" && !shouldHideDownload && (
                <li>
                  <button
                    className="dropdown-item d-flex align-items-center"
                    style={{ maxWidth: "100%" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction("download", item);
                    }}
                  >
                    <Download size={16} /> Download
                  </button>
                </li>
              )}
              {item.type === "file" &&
                (item.isPersonalSpace || showChangeStatus) && (
                  <li>
                    <button
                      className="dropdown-item  d-flex align-items-center"
                      style={{ maxWidth: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.isPersonalSpace) {
                          if (item.fileStatus === "1") {
                            onAction("process", item);
                          } else {
                            onAction("revert", item);
                          }
                        } else {
                          onAction("status", item);
                        }
                      }}
                    >
                      {item.isPersonalSpace ? (
                        item.fileStatus === "1" ? (
                          <>
                            <Upload size={16} className="" /> Train File
                          </>
                        ) : (
                          <>
                            <RefreshCw size={16} className="" />{" "}
                            Restore File
                          </>
                        )
                      ) : (
                        <>
                          <Settings size={16} className="" /> Change
                          Status
                        </>
                      )}
                    </button>
                  </li>
                )}

              
              {canModify && (
                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center"
                      style={{ maxWidth: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction("move", item);
                      }}
                    >
                      <FolderTree size={16} /> Move
                    </button>
                  </li>
                  
              )}
              {(item.isPersonalSpace || isPersonalDeletableItem) && (
                <li className="">
                  <button
                    className="dropdown-item text-danger d-flex align-items-center"
                    style={{ maxWidth: "100%" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction("delete", item);
                    }}
                  >
                    <Trash2 size={16} className="text-danger" />{" "}
                    {item.fileStatus === "0" ? "Delete" : "Delete"}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <tr
      ref={combinedRef}
      data-item-id={String(item.id)}
      className={`repo-list-row ${isSelected ? "selected" : ""} ${isOver ? "drop-target" : ""} ${!canModify ? "read-only" : ""}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <td style={{ width: "40px" }} onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          disabled={isBlockedDocumentSelection}
          aria-disabled={isBlockedDocumentSelection}
          onChange={() => {
            if (!isBlockedDocumentSelection) {
              onToggleSelect(item.id);
            }
          }}
          style={{
            cursor: isBlockedDocumentSelection ? "not-allowed" : "pointer",
            opacity: isBlockedDocumentSelection ? 0.45 : 1,
          }}
        />
      </td>
      <td
        onClick={(e) => {
          if (isBlockedDocumentSelection) {
            return;
          }
          if (dragSelectionRef.current) {
            return;
          }
          // For folders, debounce to wait for potential double-click
          if (item.type === "folder") {
            if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
            clickTimerRef.current = setTimeout(() => {
              onToggleSelect(item.id);
              clickTimerRef.current = null;
            }, 300);
          } else {
            onToggleSelect(item.id);
          }
        }}
        onDoubleClick={(e) => {
          if (item.type === "folder") {
            // Clear pending single-click selection
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = null;
            }
            onNavigate(item.id, item.name);
          }
        }}
        style={{ cursor: "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {item.type === "folder" ? (
            <Folder size={18} className="repo-folder-icon" />
          ) : (
            getFileIcon(item, 18)
          )}
          <span
            style={{
              maxWidth: "250px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.name}
          >
            {item.name}
          </span>
          {/* {item.mode === "global" && (
            <span className="badge bg-info ms-2" >
              Global
            </span>
          )} */}
        </div>
      </td>
      <td>{getItemTypeDisplay(item)}</td>
      <td>
        {item.type === "file"
          ? item.size
            ? formatBytes(item.size)
            : "0 Bytes"
          : ""}
      </td>
      <td>
        {item.type === "file" &&
        item.fileType !== "text" &&
        item.fileType !== "qa" ? (
          role === "admin" ? (
            <select
              className={`badge repo-approval-badge ${
                item.approvalStatus === "approved"
                  ? "bg-success"
                  : item.approvalStatus === "duplicate"
                    ? "bg-duplicate"
                    : item.approvalStatus === "pending"
                      ? "bg-warning"
                      : "bg-danger"
              }`}
              value={item.approvalStatus || "pending"}
              onChange={(e) => {
                e.stopPropagation();
                onApprovalStatusChange &&
                  onApprovalStatusChange(item.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                cursor: "pointer",
                outline: "none",
                opacity: updatingItems.has(item.id) ? 0.5 : 1,
              }}
              disabled={updatingItems.has(item.id)}
            >
              <option value={item.approvalStatus || "pending"} hidden>
                {(item.approvalStatus || "pending").charAt(0).toUpperCase() +
                  (item.approvalStatus || "pending").slice(1)}
              </option>

              {item.approvalStatus !== "pending" && (
                <option value="pending">Pending</option>
              )}

              {item.approvalStatus !== "approved" && (
                <option value="approved">Approved</option>
              )}

              {item.approvalStatus !== "rejected" && (
                <option value="rejected">Rejected</option>
              )}

              {item.approvalStatus !== "duplicate" && (
                <option value="duplicate">Duplicate</option>
              )}
            </select>
          ) : (
            <span
              className={`badge repo-approval-badge ${
                item.approvalStatus === "approved"
                  ? "bg-success"
                  : item.approvalStatus === "duplicate"
                    ? "bg-duplicate"
                    : item.approvalStatus === "pending"
                      ? "bg-warning"
                      : "bg-danger"
              }`}
            >
              {(item.approvalStatus || "Pending").charAt(0).toUpperCase() +
                (item.approvalStatus || "Pending").slice(1)}
            </span>
          )
        ) : (
          ""
        )}
      </td>

      <td>
        {item.type === "file" && (
          <span
            className={`badge repo-status-badge ${
              item.fileStatus === "1" ? "bg-active" : "bg-inactive"
            }`}
          >
            {item.fileStatus === "1" ? "Active" : "Inactive"}
          </span>
        )}
      </td>
      <td>
        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}
      </td>
      <td>
        {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "N/A"}
      </td>
      {role === "admin" && <td>{item.uploadedBy || "N/A"}</td>}
      <td className="text-center align-middle action-cell">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.2rem",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Download */}
          <div
            style={{ width: "20px", display: "flex", justifyContent: "center" }}
          >
            {item.type === "file" && !shouldHideDownload && (
              <Download
                size={16}
                onClick={() => onAction("download", item)}
                style={{ cursor: "pointer" }}
                title="Download"
              />
            )}
          </div>

          {/* Train / Restore / Change Status */}
          <div
            style={{ width: "20px", display: "flex", justifyContent: "center" }}
          >
            {item.type === "file" &&
              (item.isPersonalSpace || showChangeStatus) &&
              (item.isPersonalSpace ? (
                item.fileStatus === "1" ? (
                  <Upload
                    size={16}
                    onClick={() => onAction("process", item)}
                    style={{ cursor: "pointer" }}
                    title="Train File"
                  />
                ) : (
                  <RefreshCw
                    size={16}
                    onClick={() => onAction("revert", item)}
                    style={{ cursor: "pointer" }}
                    className="icon-image"
                    title="Restore File"
                  />
                )
              ) : (
                <RefreshCw
                  size={16}
                  className=""
                  onClick={() => {
                    if (item.approvalStatus !== "duplicate") {
                      onAction("status", item);
                    }
                  }}
                  style={{
                    cursor:
                      item.approvalStatus === "duplicate"
                        ? "not-allowed"
                        : "pointer",
                    opacity: item.approvalStatus === "duplicate" ? 0.5 : 1,
                  }}
                  title="Change Status"
                />
              ))}
          </div>

          {/* Move */}
          <div
            style={{ width: "20px", display: "flex", justifyContent: "center" }}
          >
            {canModify && (
              <FolderTree
                size={16}
                className="icon-image"
                onClick={() => onAction("move", item)}
                style={{ cursor: "pointer" }}
                title="Move"
              />
            )}
          </div>

          {/* Delete */}
          <div
            style={{ width: "20px", display: "flex", justifyContent: "center" }}
          >
            {(item.isPersonalSpace || isPersonalDeletableItem) && (
              <Trash2
                size={16}
                className="text-danger"
                style={{ cursor: "pointer" }}
                onClick={() => onAction("delete", item)}
                title={item.fileStatus === "0" ? "Delete" : "Delete"}
              />
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};
